import { useEffect, useMemo, useState } from "react";
import {
  App,
  Button,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Channel, ChannelAffiliateConfig } from "@glow-price/shared";
import { CHANNEL_TYPES, ENTITY_STATUSES, TRUST_LEVELS } from "@glow-price/shared";

import {
  affiliateNetworksApi,
  channelAffiliateConfigsApi,
  channelProductsApi,
  channelScrapersApi,
  channelsApi,
  type ChannelScraperRow,
} from "../api/resources";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";

import { AliasesInput } from "../components/AliasesInput";
import { EmptyState, EnhancedTable, PageHeader } from "../components";

const TYPE_OPTIONS = Object.values(CHANNEL_TYPES).map((v) => ({ value: v, label: v }));
const TRUST_OPTIONS = Object.values(TRUST_LEVELS).map((v) => ({ value: v, label: v }));
const STATUS_OPTIONS = Object.values(ENTITY_STATUSES).map((v) => ({ value: v, label: v }));

type ScraperDot = "enabled" | "paused" | "disabled" | "none";

const DOT_COLOR: Record<ScraperDot, string> = {
  enabled: "#52c41a",
  paused: "#fa8c16",
  disabled: "#bfbfbf",
  none: "#d9d9d9",
};

const DOT_LABEL: Record<ScraperDot, string> = {
  enabled: "已启用",
  paused: "已暂停",
  disabled: "已停用",
  none: "未配置",
};

function ScraperDotIndicator({ state }: { state: ScraperDot }) {
  return (
    <Space size={6}>
      <span
        style={{
          display: "inline-block",
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: DOT_COLOR[state],
        }}
      />
      <Typography.Text type={state === "none" ? "secondary" : undefined}>
        {DOT_LABEL[state]}
      </Typography.Text>
    </Space>
  );
}

type ChannelFormValues = Partial<Channel>;

export function ChannelsPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Channel | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [form] = Form.useForm<ChannelFormValues>();

  const list = useQuery({
    queryKey: ["channels", q, page],
    queryFn: () => channelsApi.list({ q, page, pageSize: 20 }),
  });

  // Side-channel data for the two derived columns. Both endpoints are admin
  // list APIs we already use elsewhere — kept on long stale time since they
  // change slowly relative to the channels table.
  const scrapersQuery = useQuery({
    queryKey: ["channel-scrapers", "for-channels"],
    queryFn: () => channelScrapersApi.list(),
    staleTime: 30_000,
  });

  // Fetch ALL channel_products in one shot so we can derive per-channel counts
  // without N round-trips. The admin dataset is bounded (≤ a few thousand)
  // and the response already supports pageSize.
  const cpCountsQuery = useQuery({
    queryKey: ["channel-products", "counts-by-channel"],
    queryFn: async () => {
      const res = await channelProductsApi.list({ page: 1, pageSize: 5000 });
      const counts: Record<string, number> = {};
      for (const cp of res.items) {
        counts[cp.channelId] = (counts[cp.channelId] ?? 0) + 1;
      }
      return counts;
    },
    staleTime: 60_000,
  });

  const scraperByChannel = useMemo(() => {
    const map = new Map<string, ScraperDot>();
    for (const row of scrapersQuery.data?.items ?? []) {
      if (!row.scraper) {
        map.set(row.channel.id, "none");
        continue;
      }
      if (row.scraper.pausedAt) map.set(row.channel.id, "paused");
      else if (row.scraper.enabled) map.set(row.channel.id, "enabled");
      else map.set(row.channel.id, "disabled");
    }
    return map;
  }, [scrapersQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async (values: Partial<Channel>) => {
      if (editing) return channelsApi.update(editing.id, values);
      return channelsApi.create(values);
    },
    onSuccess: () => {
      message.success("已保存");
      qc.invalidateQueries({ queryKey: ["channels"] });
      setDrawerOpen(false);
      setEditing(null);
    },
    onError: (err: Error) => message.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => channelsApi.remove(id),
    onSuccess: () => {
      message.success("已删除");
      qc.invalidateQueries({ queryKey: ["channels"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  // Reset drawer form + tab when opening/closing.
  useEffect(() => {
    if (drawerOpen) {
      setActiveTab("basic");
      form.resetFields();
      form.setFieldsValue(
        (editing ?? {
          type: CHANNEL_TYPES.DUTY_FREE,
          trustLevel: TRUST_LEVELS.HIGH,
          trustTags: [],
          supportsAffiliate: false,
          status: ENTITY_STATUSES.ACTIVE,
        }) as ChannelFormValues,
      );
    }
  }, [drawerOpen, editing, form]);

  const items = list.data?.items ?? [];
  const showEmpty = !list.isLoading && items.length === 0;

  const handleSave = async () => {
    const values = await form.validateFields();
    await saveMutation.mutateAsync(values);
  };

  return (
    <div>
      <PageHeader
        title="渠道管理"
        description="维护销售渠道（免税店 / 官方 / 平台）的基础信息、可信等级与联盟支持。"
        stats={[{ label: `共 ${list.data?.total ?? 0} 个渠道` }]}
        extra={
          <Space>
            <Input.Search
              placeholder="搜索名称或代号"
              allowClear
              onSearch={(v) => {
                setPage(1);
                setQ(v);
              }}
              style={{ width: 240 }}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null);
                setDrawerOpen(true);
              }}
            >
              新建渠道
            </Button>
          </Space>
        }
      />

      {showEmpty ? (
        <EmptyState
          title="暂无渠道"
          description="尚未创建任何销售渠道。点击「新建渠道」开始添加。"
          action={
            <Button
              type="primary"
              onClick={() => {
                setEditing(null);
                setDrawerOpen(true);
              }}
            >
              新建渠道
            </Button>
          }
        />
      ) : (
        <EnhancedTable<Channel>
          rowKey="id"
          loading={list.isLoading}
          dataSource={items}
          persistKey="channels"
          exportFilename="channels"
          pagination={{
            current: page,
            pageSize: 20,
            total: list.data?.total ?? 0,
            onChange: (p) => setPage(p),
            showSizeChanger: false,
          }}
          columns={[
            { title: "名称", dataIndex: "name" },
            { title: "代号", dataIndex: "code", width: 120 },
            { title: "类型", dataIndex: "type", width: 120 },
            { title: "可信", dataIndex: "trustLevel", width: 80, render: (v) => <Tag>{v}</Tag> },
            {
              title: "Scraper",
              key: "scraper",
              width: 110,
              render: (_, row) => (
                <ScraperDotIndicator state={scraperByChannel.get(row.id) ?? "none"} />
              ),
            },
            {
              title: "SKU 数",
              key: "sku-count",
              width: 80,
              align: "right",
              render: (_, row) => cpCountsQuery.data?.[row.id] ?? 0,
            },
            {
              title: "productUrlTemplate",
              dataIndex: "productUrlTemplate",
              width: 200,
              render: (_, row) => {
                if (!row.productUrlTemplate) {
                  return <Typography.Text type="secondary">—</Typography.Text>;
                }
                return (
                  <Tooltip title={row.productUrlTemplate}>
                    <Typography.Text
                      code
                      style={{ fontSize: 12 }}
                      ellipsis
                    >
                      {row.productUrlTemplate}
                    </Typography.Text>
                  </Tooltip>
                );
              },
            },
            {
              title: "支持 CPS",
              dataIndex: "supportsAffiliate",
              width: 90,
              render: (v: boolean) => (v ? "是" : "否"),
            },
            {
              title: "操作",
              key: "actions",
              width: 100,
              render: (_, row) => (
                <Space size={4}>
                  <Tooltip title="编辑">
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setEditing(row);
                        setDrawerOpen(true);
                      }}
                    />
                  </Tooltip>
                  <Popconfirm title="确认删除？" onConfirm={() => deleteMutation.mutate(row.id)}>
                    <Tooltip title="删除">
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Tooltip>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      )}

      <Drawer
        open={drawerOpen}
        title={editing ? `编辑渠道 - ${editing.name}` : "新建渠道"}
        width={640}
        destroyOnClose
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
        extra={
          <Space>
            <Button
              onClick={() => {
                setDrawerOpen(false);
                setEditing(null);
              }}
            >
              取消
            </Button>
            <Button
              type="primary"
              loading={saveMutation.isPending}
              onClick={handleSave}
            >
              保存
            </Button>
          </Space>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "basic",
              label: "基本信息",
              children: (
                <Form layout="vertical" form={form}>
                  <Form.Item name="name" label="名称" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="code" label="代号" rules={[{ required: true }]}>
                    <Input placeholder="cdf_japan / cdf / hailv / self ..." />
                  </Form.Item>
                  <Form.Item name="type" label="类型" rules={[{ required: true }]}>
                    <Select options={TYPE_OPTIONS} />
                  </Form.Item>
                  <Form.Item name="trustLevel" label="可信等级">
                    <Select options={TRUST_OPTIONS} />
                  </Form.Item>
                  <Form.Item name="trustTags" label="可信标签">
                    <AliasesInput placeholder="免税渠道、官方、品牌授权..." />
                  </Form.Item>
                  <Form.Item name="appId" label="目标小程序 AppId">
                    <Input placeholder="wxXXXXXX" />
                  </Form.Item>
                  <Form.Item name="defaultPathTemplate" label="默认 path 模板">
                    <Input placeholder="/pages/product/detail?id={{channelProductId}}&click_id={{clickId}}" />
                  </Form.Item>
                  <Form.Item
                    name="productUrlTemplate"
                    label="商品详情 URL 模板"
                    extra="HtmlScraper 用于推导每个 SKU 的目标 URL。用 {{sku}} 占位渠道商品 ID。免税 / 钱包渠道可留空。"
                  >
                    <Input.TextArea
                      rows={2}
                      placeholder="https://www.example.com/p/{{sku}}.html"
                      style={{ fontFamily: "monospace", fontSize: 12 }}
                    />
                  </Form.Item>
                  <Form.Item name="supportsAffiliate" label="支持 CPS" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                  <Form.Item name="status" label="状态">
                    <Select options={STATUS_OPTIONS} />
                  </Form.Item>
                </Form>
              ),
            },
            {
              key: "scraper",
              label: "采集配置",
              disabled: !editing,
              children: editing ? (
                <ChannelScraperTab channelId={editing.id} />
              ) : (
                <Typography.Text type="secondary">请先保存渠道再配置采集</Typography.Text>
              ),
            },
            {
              key: "affiliate",
              label: "联盟",
              disabled: !editing,
              children: editing ? (
                <ChannelAffiliateTab channelId={editing.id} />
              ) : (
                <Typography.Text type="secondary">请先保存渠道再配置联盟</Typography.Text>
              ),
            },
          ]}
        />
      </Drawer>
    </div>
  );
}

// ─── 采集配置 tab ──────────────────────────────────────────────────────────
function ChannelScraperTab({ channelId }: { channelId: string }) {
  const scrapersQuery = useQuery({
    queryKey: ["channel-scrapers", "for-channels"],
    queryFn: () => channelScrapersApi.list(),
    staleTime: 30_000,
  });

  const row: ChannelScraperRow | undefined = scrapersQuery.data?.items.find(
    (r) => r.channel.id === channelId,
  );

  if (scrapersQuery.isLoading) {
    return <Typography.Text type="secondary">加载中…</Typography.Text>;
  }
  if (!row?.scraper) {
    return (
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        <Typography.Text type="secondary">该渠道尚未配置采集任务。</Typography.Text>
        <Typography.Text type="secondary">
          请前往「采集管理」页面创建配置。
        </Typography.Text>
      </Space>
    );
  }

  const s = row.scraper;
  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Typography.Text type="secondary">
        采集任务详情。修改请前往「采集管理」页面。
      </Typography.Text>
      <Form layout="vertical" disabled>
        <Form.Item label="采集类型">
          <Tag>{s.scraperType}</Tag>
        </Form.Item>
        <Form.Item label="启用">
          <Switch checked={s.enabled} />
        </Form.Item>
        <Form.Item label="调度 cron">
          <Input value={s.scheduleCron} />
        </Form.Item>
        <Form.Item label="最大并发抓取数">
          <Input value={String(s.maxConcurrentFetches)} />
        </Form.Item>
        <Form.Item label="连续失败告警阈值">
          <Input value={String(s.alertAfterFailures)} />
        </Form.Item>
        <Form.Item label="connections">
          <Input value={s.pausedAt ? `已暂停: ${s.pausedReason ?? "—"}` : "正常"} />
        </Form.Item>
      </Form>
    </Space>
  );
}

// ─── 联盟 tab ─────────────────────────────────────────────────────────────
function ChannelAffiliateTab({ channelId }: { channelId: string }) {
  const configsQuery = useQuery({
    queryKey: ["channel-affiliate-configs", channelId],
    queryFn: () => channelAffiliateConfigsApi.list({ channelId, pageSize: 100 }),
  });

  const networksQuery = useQuery({
    queryKey: ["affiliate-networks", "all-for-channel"],
    queryFn: () => affiliateNetworksApi.list({ pageSize: 200 }),
    staleTime: 60_000,
  });

  const networkById = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of networksQuery.data?.items ?? []) m.set(n.id, n.name);
    return m;
  }, [networksQuery.data]);

  const items = configsQuery.data?.items ?? [];

  if (configsQuery.isLoading) {
    return <Typography.Text type="secondary">加载中…</Typography.Text>;
  }

  if (items.length === 0) {
    return (
      <EmptyState
        title="暂无联盟配置"
        description="该渠道尚未绑定任何联盟。请前往「渠道联盟配置」页面添加。"
      />
    );
  }

  return (
    <Table<ChannelAffiliateConfig>
      rowKey="id"
      size="small"
      pagination={false}
      dataSource={items}
      columns={[
        {
          title: "联盟",
          dataIndex: "networkId",
          render: (id: string) => networkById.get(id) ?? id.slice(0, 8),
        },
        { title: "PID", dataIndex: "pid", ellipsis: true },
        { title: "状态", dataIndex: "status", width: 100 },
      ]}
    />
  );
}
